import { Link, useRouterState } from "@tanstack/react-router";
import { Logo } from "./Logo";
import { detectLocale, t, pathMap } from "@/lib/locale";
import { openCookieSettings } from "./CookieConsent";
import { trackConversion } from "@/lib/analytics";

export function Footer() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const locale = detectLocale(path);
  const tr = t[locale];

  const services =
    locale === "en"
      ? [
          { to: "/en/services/performance-marketing", label: "Performance Marketing" },
          { to: "/en/services/social-media-marketing", label: "Social Media Marketing" },
          { to: "/en/services/seo-ai-seo", label: "SEO & AI SEO" },
          { to: "/en/services/geo-generative-engine-optimization", label: "GEO — Generative Engine Optimization" },
          { to: "/en/services/conversion-optimization", label: "Conversion & Web Optimization" },
          { to: "/en/services/conversion-rate-optimization", label: "Conversion Rate Optimization" },
          { to: "/en/services/tracking-data-foundation", label: "Tracking & Data Foundation" },
          { to: "/en/services/strategic-coordination", label: "Strategic Sparring & Coordination" },
        ]
      : [
          { to: "/leistungen/performance-marketing", label: "Performance Marketing" },
          { to: "/leistungen/social-media-marketing", label: "Social Media Marketing" },
          { to: "/leistungen/seo-ai-seo", label: "SEO & AI SEO" },
          { to: "/leistungen/geo-generative-engine-optimization", label: "GEO — Generative Engine Optimization" },
          { to: "/leistungen/conversion-web-optimierung", label: "Conversion- & Web-Optimierung" },
          { to: "/leistungen/conversion-rate-optimierung", label: "Conversion-Rate-Optimierung" },
          { to: "/leistungen/tracking-datenbasis", label: "Tracking & Datenbasis" },
          { to: "/leistungen/strategie-koordination", label: "Strategie & Koordination" },
        ];

  const development =
    locale === "en"
      ? [
          { to: "/en/services/website-development", label: "Website Development" },
          { to: "/en/services/ecommerce-development", label: "E-Commerce Development" },
          { to: "/en/services/digital-development", label: "Digital Services Development" },
        ]
      : [
          { to: "/leistungen/website-entwicklung", label: "Website-Entwicklung" },
          { to: "/leistungen/onlineshop-entwicklung", label: "Onlineshop-Entwicklung" },
          { to: "/leistungen/entwicklung-digitaler-dienste", label: "Entwicklung digitaler Dienste" },
        ];

  const developmentLabel = locale === "en" ? "Development" : "Entwicklung";


  const company =
    locale === "en"
      ? [
          { to: pathMap.about.en, label: "About" },
          { to: pathMap.regions.en, label: "Regions" },
          { to: pathMap.contact.en, label: "Contact" },
        ]
      : [
          { to: pathMap.about.de, label: "Über uns" },
          { to: pathMap.regions.de, label: "Regionen" },
          { to: pathMap.contact.de, label: "Kontakt" },
        ];

  const knowledge =
    locale === "en"
      ? [
          { to: pathMap.blog.en, label: "Blog" },
          { to: pathMap.glossary.en, label: "Glossary" },
        ]
      : [
          { to: pathMap.blog.de, label: "Blog" },
          { to: pathMap.glossary.de, label: "Glossar" },
        ];

  const knowledgeLabel = locale === "en" ? "Knowledge" : "Wissen";

  const legal =
    locale === "en"
      ? [
          { to: pathMap.imprint.en, label: "Imprint" },
          { to: pathMap.privacy.en, label: "Privacy" },
          { to: pathMap.accessibility.en, label: "Accessibility" },
        ]
      : [
          { to: pathMap.imprint.de, label: "Impressum" },
          { to: pathMap.privacy.de, label: "Datenschutz" },
          { to: pathMap.accessibility.de, label: "Barrierefreiheit" },
        ];

  const products =
    locale === "en"
      ? [
          { to: "/en/services/tools", label: "All products" },
          { to: "/en/services/delta", label: "Delta" },
          { to: "/en/services/picazzoo", label: "picazzoo" },
          { to: "/en/services/gonzalads", label: "GonzalAds" },
        ]
      : [
          { to: "/leistungen/tools", label: "Alle Produkte" },
          { to: "/leistungen/delta", label: "Delta" },
          { to: "/leistungen/picazzoo", label: "picazzoo" },
          { to: "/leistungen/gonzalads", label: "GonzalAds" },
        ];

  const productsLabel = locale === "en" ? "Products" : "Produkte";

  const werkzeuge =
    locale === "en"
      ? [
          { to: "/en/tools", label: "All tools" },
          { to: "/en/tools/lighthouse-check", label: "Lighthouse check" },
          { to: "/en/tools/profitability-calculator", label: "Viability calculator" },
          { to: "/en/tools/performance-marketing-checklist", label: "Performance marketing checklist" },
        ]
      : [
          { to: "/tools", label: "Alle Werkzeuge" },
          { to: "/tools/lighthouse-check", label: "Lighthouse-Check" },
          { to: "/tools/wirtschaftlichkeitsrechner", label: "Wirtschaftlichkeits-Rechner" },
          { to: "/tools/performance-marketing-checkliste", label: "Performance-Marketing-Checkliste" },
        ];

  const werkzeugeLabel = locale === "en" ? "Tools" : "Werkzeuge";
  const werkzeugeHref = locale === "en" ? "/en/tools" : "/tools";

  const isEn = locale === "en";
  const servicesHref = locale === "en" ? pathMap.services.en : pathMap.services.de;
  const productsHref = locale === "en" ? "/en/services/tools" : "/leistungen/tools";
  const companyHref = locale === "en" ? pathMap.about.en : pathMap.about.de;

  return (
    <footer className="bg-bg-deep text-white">
      <div className="mx-auto max-w-[1200px] px-6 py-20 md:px-12">
        <div className="grid gap-12 md:grid-cols-3">
          <div className="space-y-4">
            <Logo tone="white" />
            <p className="text-sm text-white/85 leading-relaxed">
              {tr.footer.tagline}<br />{tr.footer.city}
            </p>
          </div>
          <address className="not-italic text-sm leading-relaxed text-white/80">
            <div>Calvarius GmbH</div>
            <div>Kalvarienberg 20</div>
            <div>97753 Karlstadt am Main</div>
            <div>{isEn ? "Germany" : "Deutschland"}</div>
            <div className="mt-3 space-y-1">
              <a
                href="tel:+4993539768000"
                onClick={() => trackConversion("phone_number_clicked", { location: "footer" })}
                className="block py-2 hover:text-lime transition-colors"
              >
                +49 9353 9768-000
              </a>
              <a
                href="mailto:info@calvarius.com"
                className="block py-2 hover:text-lime transition-colors"
              >
                info@calvarius.com
              </a>
            </div>
          </address>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/60">
              {isEn ? "Service area (driving distance)" : "Einzugsgebiet (Anfahrt)"}
            </div>
            <div className="mt-2 font-mono text-[11px] leading-relaxed text-white/70">
              Würzburg (25 km) · Schweinfurt (40 km) · Aschaffenburg (50 km) · Frankfurt (100 km) · Nürnberg (100 km) · {isEn ? "nationwide digital" : "bundesweit digital"}
            </div>
          </div>
        </div>
        <div className="mt-16 grid gap-12 border-t border-white/10 pt-16 md:grid-cols-3">
          <div className="space-y-10">
            <FooterCol title={tr.footer.services} titleHref={servicesHref} items={services} />
            <FooterCol title={developmentLabel} items={development} />
          </div>
          <div className="space-y-10">
            <FooterCol title={productsLabel} titleHref={productsHref} items={products} />
            <FooterCol title={werkzeugeLabel} titleHref={werkzeugeHref} items={werkzeuge} />
            <FooterCol title={knowledgeLabel} items={knowledge} />
          </div>
          <div className="space-y-10">
            <FooterCol title={tr.footer.company} titleHref={companyHref} items={company} />
            <FooterCol title={tr.footer.legal} items={legal} />
          </div>
        </div>
        <div className="mt-16 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 font-mono text-xs text-white/80 md:flex-row md:items-center">
          <span>© 2026 Calvarius GmbH</span>
          <div className="flex items-center gap-5">
            <button
              type="button"
              onClick={openCookieSettings}
              className="hover:text-lime transition-colors"
            >
              {isEn ? "Cookie settings" : "Cookie-Einstellungen"}
            </button>
            <span className="flex items-center gap-2">
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-lime live-pulse" />
              Karlstadt · {isEn ? "Germany" : "Deutschland"}
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
}

function FooterCol({
  title,
  titleHref,
  items,
  columns = 1,
  className = "",
}: {
  title: string;
  titleHref?: string;
  items: { to: string; label: string }[];
  columns?: 1 | 2;
  className?: string;
}) {
  const headingClass = "mb-4 font-mono text-xs uppercase tracking-[0.18em] text-white/80";
  return (
    <div className={className}>
      {titleHref ? (
        <h2 className={headingClass}>
          <Link to={titleHref} className="hover:text-lime transition-colors">
            {title}
          </Link>
        </h2>
      ) : (
        <h2 className={headingClass}>{title}</h2>
      )}
      <ul
        className={
          columns === 2
            ? "grid grid-cols-1 gap-x-6 gap-y-2.5 text-sm text-white/80 sm:grid-cols-2"
            : "space-y-2.5 text-sm text-white/80"
        }
      >
        {items.map((i) => (
          <li key={i.to}>
            <Link to={i.to} className="hover:text-lime transition-colors">
              {i.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
